import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';

import { ModalComponent } from '../../../../../shared/components/ui/modal/modal.component';
import { FormBuilderService, FormBuilderState } from '../../../service/form-builder.service';
import { FormFieldModel } from '../../../models/form-field.model';
import { FormStepConfig } from '../../../models/form.model';
import { COUNTRIES_DATA, CountryData } from '../../../../../shared/data/countries.data';
import { evaluateRule, parseConditionalRule, FormValues } from '../../../utils/condition-evaluator';
import { parseStepsConfig, parseStepRoutingConfig, resolveNextStep } from '../../../utils/step-routing';
import { CountrySelectComponent } from '../../../../../shared/components/form/country-select/country-select.component';

@Component({
  selector: 'app-form-preview',
  standalone: true,
  imports: [CommonModule, ModalComponent, CountrySelectComponent],
  templateUrl: './form-preview.component.html',
})
export class FormPreviewComponent implements OnInit, OnDestroy {
  private _isOpen = false;
  @Input() set isOpen(value: boolean) {
    if (value && !this._isOpen) {
      this.previewValues = {};
      this.previewNpsSelected = {};
      this.previewStep = 1;
      this.stepHistory = [];
    }
    this._isOpen = value;
  }
  get isOpen(): boolean { return this._isOpen; }
  @Output() closePreview = new EventEmitter<void>();

  private readonly destroy$ = new Subject<void>();
  state!: FormBuilderState;
  previewStep = 1;
  private stepHistory: number[] = [];
  previewMode: 'desktop' | 'mobile' = 'desktop';
  readonly countries: CountryData[] = COUNTRIES_DATA;
  readonly npsScores = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  previewValues: FormValues = {};
  previewNpsSelected: Record<string, number | null> = {};

  constructor(readonly builder: FormBuilderService) {}

  ngOnInit(): void {
    this.builder.state.pipe(takeUntil(this.destroy$)).subscribe(s => {
      const prevSettings = this.state?.settings;
      this.state = s;
      // Solo resetear el paso cuando cambia la configuración multi-step,
      // no en cada cambio de campo/label/etc.
      if (
        s.settings?.isMultiStep !== prevSettings?.isMultiStep ||
        s.settings?.totalSteps  !== prevSettings?.totalSteps
      ) {
        this.previewStep = 1;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isMultiStep(): boolean {
    return !!(this.state?.settings?.isMultiStep && (this.state?.settings?.totalSteps ?? 0) > 1);
  }

  get totalSteps(): number {
    return this.state?.settings?.totalSteps ?? 1;
  }

  get stepsArray(): number[] {
    return Array.from({ length: this.totalSteps }, (_, i) => i + 1);
  }

  // ── Config por paso (inc22) ────────────────────────────────────────────────

  private get stepsConfig(): FormStepConfig[] {
    return parseStepsConfig(this.state?.settings?.stepsConfig);
  }

  private currentStepConfig(): FormStepConfig | undefined {
    if (!this.isMultiStep) return undefined;
    return this.stepsConfig.find(s => s.step === this.previewStep);
  }

  get stepTitle(): string {
    return this.currentStepConfig()?.title?.trim() || '';
  }

  get stepDescription(): string {
    return this.currentStepConfig()?.description?.trim() || '';
  }

  get stepImageUrl(): string {
    return this.currentStepConfig()?.imageUrl?.trim() || '';
  }

  get visibleFields(): FormFieldModel[] {
    const all = (this.state?.fields ?? []).filter(f => !f.isHidden);
    const stepFiltered = this.isMultiStep
      ? all.filter(f => (f.stepNumber ?? 1) === this.previewStep)
      : all;
    return stepFiltered.filter(f => {
      const rule = parseConditionalRule(f.conditionalRules);
      if (!rule) return true;
      const applies = evaluateRule(rule, this.previewValues);
      if (rule.action === 'show') return applies;
      if (rule.action === 'hide') return !applies;
      return true; // action === 'required': always visible
    });
  }

  isFieldRequired(field: FormFieldModel): boolean {
    if (field.isRequired) return true;
    const rule = parseConditionalRule(field.conditionalRules);
    if (!rule || rule.action !== 'required') return false;
    return evaluateRule(rule, this.previewValues);
  }

  setFieldValue(key: string, value: string): void {
    this.previewValues = { ...this.previewValues, [key]: value };
  }

  toggleCheckboxValue(key: string, optionValue: string, checked: boolean): void {
    const current = this.previewValues[key];
    const arr: string[] = Array.isArray(current) ? [...current] : [];
    if (checked) {
      if (!arr.includes(optionValue)) arr.push(optionValue);
    } else {
      const idx = arr.indexOf(optionValue);
      if (idx !== -1) arr.splice(idx, 1);
    }
    this.previewValues = { ...this.previewValues, [key]: arr };
  }

  selectNps(key: string, score: number): void {
    this.previewNpsSelected = { ...this.previewNpsSelected, [key]: score };
    this.setFieldValue(key, score.toString());
  }

  prevStep(): void {
    // inc23: volver al paso anterior real (respeta los saltos condicionales).
    if (this.stepHistory.length) {
      this.previewStep = this.stepHistory.pop()!;
    } else if (this.previewStep > 1) {
      this.previewStep--;
    }
  }

  nextStep(): void {
    // inc23: evalúa las reglas de salto condicional para decidir el siguiente paso.
    const routing = parseStepRoutingConfig(this.state?.settings?.stepRoutingConfig);
    const next = resolveNextStep(routing, this.previewStep, this.previewValues, this.totalSteps);
    if (next !== this.previewStep) {
      this.stepHistory.push(this.previewStep);
      this.previewStep = next;
    }
  }

  columnClass(span: number): string {
    const map: Record<number, string> = { 1: 'col-span-1', 2: 'col-span-2', 3: 'col-span-3' };
    return map[span] ?? 'col-span-3';
  }

  // ── Style config aplicado ───────────────────────────────────────────────

  get style() {
    const raw = this.state?.settings?.styleConfig;
    if (!raw) return {} as Record<string, any>;
    try {
      return JSON.parse(raw) as Record<string, any>;
    } catch {
      return {} as Record<string, any>;
    }
  }

  get submitLabel(): string {
    return this.state?.settings?.submitButtonLabel?.trim() || 'Enviar';
  }

  get containerStyle() {
    const s = this.style;
    return {
      backgroundColor: s['backgroundColor'] || undefined,
      color: s['textColor'] || undefined,
      fontFamily: s['fontFamily'] || undefined,
      fontSize: s['fontSize'] ? `${s['fontSize']}px` : undefined,
    };
  }

  get inputStyle() {
    const s = this.style;
    return {
      borderColor: s['borderColor'] || undefined,
      borderRadius: s['borderRadius'] != null ? `${s['borderRadius']}px` : undefined,
      color: s['textColor'] || undefined,
    };
  }

  get labelStyle() {
    return { color: this.style['labelColor'] || undefined };
  }

  get buttonStyle() {
    const s = this.style;
    return {
      backgroundColor: s['primaryColor'] || null,
      color: s['buttonTextColor'] || null,
      borderRadius: s['borderRadius'] != null ? `${s['borderRadius']}px` : null,
    };
  }
}
